import { Dimensions, DimensionsImperial, UnitConversion } from '../types';

// MM to Inches conversion factor
const MM_TO_INCHES = 0.0393701;
const INCHES_TO_MM = 25.4;

// Convert mm to various units
export const mmToUnits = (mm: number): UnitConversion => ({
  mm,
  cm: mm / 10,
  inches: mm * MM_TO_INCHES,
});

// Convert inches to mm
export const inchesToMm = (inches: number): number => {
  return inches * INCHES_TO_MM;
};

// Convert dimensions from mm to imperial
export const dimensionsToImperial = (dims: Dimensions): DimensionsImperial => ({
  height: dims.height * MM_TO_INCHES,
  width: dims.width * MM_TO_INCHES,
  depth: dims.depth * MM_TO_INCHES,
});

// Calculate volume in liters from mm dimensions
export const calculateVolumeLiters = (dims: Dimensions): number => {
  const volumeCubicMm = dims.height * dims.width * dims.depth;
  const volumeLiters = volumeCubicMm / 1000000; // 1 liter = 1,000,000 cubic mm
  return Math.round(volumeLiters * 10) / 10; // Round to 1 decimal
};

// Calculate volume in cubic inches
export const calculateVolumeCubicInches = (dims: Dimensions): number => {
  const imperial = dimensionsToImperial(dims);
  return Math.round(imperial.height * imperial.width * imperial.depth * 10) / 10;
};

// Format dimension with unit
export const formatDimension = (mm: number, unit: 'mm' | 'cm' | 'in'): string => {
  switch (unit) {
    case 'mm':
      return `${Math.round(mm)} mm`;
    case 'cm':
      return `${(mm / 10).toFixed(1)} cm`;
    case 'in':
      return `${(mm * MM_TO_INCHES).toFixed(1)}"`;
    default:
      return `${Math.round(mm)} mm`;
  }
};

// Format dimension as a bare number (no unit suffix) — for drawings and compact spec rows
export const formatDimensionValue = (mm: number, unit: 'mm' | 'in'): string =>
  unit === 'in' ? (mm * MM_TO_INCHES).toFixed(1) : String(Math.round(mm));

// Format a retailer price, whole units (e.g. "$129")
export const formatPrice = (price: number, currency = 'USD'): string => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(price);
  } catch {
    return `$${Math.round(price)}`;
  }
};

// Format weight with unit
export const formatWeight = (kg: number, unit: 'kg' | 'lb'): string => {
  if (unit === 'lb') {
    return `${(kg * 2.20462).toFixed(1)} lb`;
  }
  return `${kg.toFixed(1)} kg`;
};

// Format dimensions string (H x W x D)
export const formatDimensionsString = (
  dims: Dimensions,
  unit: 'mm' | 'cm' | 'in'
): string => {
  const h = formatDimension(dims.height, unit).replace(/\s*\w+$/, '');
  const w = formatDimension(dims.width, unit).replace(/\s*\w+$/, '');
  const d = formatDimension(dims.depth, unit).replace(/\s*\w+$/, '');
  const suffix = unit === 'in' ? '"' : ` ${unit}`;
  return `${h} x ${w} x ${d}${suffix}`;
};

// Get scale factor for normalized comparison
export const getScaleFactor = (
  dims: Dimensions,
  maxDimension: number = 600
): number => {
  const maxDim = Math.max(dims.height, dims.width, dims.depth);
  return maxDimension / maxDim;
};

// Scale dimensions proportionally
export const scaleDimensions = (
  dims: Dimensions,
  factor: number
): Dimensions => ({
  height: dims.height * factor,
  width: dims.width * factor,
  depth: dims.depth * factor,
});

export function getAmazonSearchUrl(manufacturer: string, name: string): string {
  const tag = import.meta.env.VITE_AMAZON_AFFILIATE_TAG;
  const query = encodeURIComponent(`${manufacturer} ${name}`);
  return `https://www.amazon.com/s?k=${query}${tag ? `&tag=${tag}` : ''}`;
}
